/**
 * Compact attachment chip list shown under chat message bubbles.
 * Each chip is clickable to download the file.
 */
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip, Loader2, Image as ImageIcon, FileText, FileSpreadsheet } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useFile } from '@/shared/files/hooks/useFiles';
import { fileApi } from '@/shared/files/services/fileApi';
import { toast } from 'sonner';

interface MessageAttachmentsProps {
  fileIds: string[];
  /** Alignment — user messages align right, assistant left */
  align?: 'start' | 'end';
}

function iconForMime(mime: string): FC<{ className?: string }> {
  if (mime.startsWith('image/')) return ImageIcon;
  if (
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mime === 'application/vnd.ms-excel' ||
    mime === 'text/csv'
  ) return FileSpreadsheet;
  if (mime === 'application/pdf' || mime.startsWith('text/')) return FileText;
  return Paperclip;
}

const AttachmentChip: FC<{ fileId: string }> = ({ fileId }) => {
  const { t } = useTranslation('aiChat');
  const { data, isLoading } = useFile(fileId);
  const file = data?.data;

  if (isLoading) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/50 px-2 py-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
      </span>
    );
  }

  if (!file) return null;

  const Icon = iconForMime(file.mime_type);

  const handleDownload = async () => {
    try {
      await fileApi.download(file.id, file.original_filename);
    } catch {
      // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
      toast.error(t('downloadFailed'));
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      title={file.original_filename}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-border bg-background/50 px-2 py-1 text-xs',
        'hover:bg-accent hover:text-accent-foreground transition-colors',
        'max-w-[220px]'
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{file.original_filename}</span>
    </button>
  );
};

export const MessageAttachments: FC<MessageAttachmentsProps> = ({ fileIds, align = 'start' }) => {
  if (!fileIds || fileIds.length === 0) return null;
  return (
    <div
      className={cn(
        'mt-2 flex flex-wrap gap-1.5',
        align === 'end' ? 'justify-end' : 'justify-start'
      )}
    >
      {fileIds.map((id) => (
        <AttachmentChip key={id} fileId={id} />
      ))}
    </div>
  );
};
